import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, XCircle, MessageSquareWarning, CalendarClock,
  PauseCircle, PlayCircle, Star, ChevronLeft,
} from "lucide-react";

const PIPELINE = ["draft", "admin_review", "interview", "changes_requested", "approved", "verified", "featured", "rejected", "suspended"];
const STATUS_CLS = {
  draft: "bg-sand-200 text-foreground/70",
  admin_review: "bg-gold-500/15 text-gold-700", interview: "bg-blue-500/15 text-blue-700",
  changes_requested: "bg-terracotta-500/15 text-terracotta-600",
  approved: "bg-emerald-500/15 text-emerald-700", verified: "bg-emerald-600/20 text-emerald-800",
  featured: "bg-purple-500/15 text-purple-700", rejected: "bg-red-500/15 text-red-600",
  suspended: "bg-slate-400/20 text-slate-600",
};

const MEDIA = (key) => `${process.env.REACT_APP_BACKEND_URL}/api/teacher-app/media/${key}`;

export default function TeacherApplications() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [interviewAt, setInterviewAt] = useState("");
  const [badges, setBadges] = useState([]);
  const [meta, setMeta] = useState(null);
  const [rateInput, setRateInput] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  const load = (f = filter) => {
    api.get(`/admin/teacher-apps${f ? `?status=${f}` : ""}`).then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load applications"));
  };
  useEffect(() => { load(); api.get("/teacher-app/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (id) => {
    try {
      const r = await api.get(`/admin/teacher-apps/${id}`);
      setDetail(r.data);
      setBadges(r.data.badges || []);
      setNote("");
      setRateInput(r.data.preferences?.hourly_rate ?? "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open application"); }
  };

  const saveRate = async () => {
    const value = rateInput === "" ? null : Number(rateInput);
    if (value !== null && (Number.isNaN(value) || value < 0 || value > 1000)) {
      return toast.error("Enter a rate between 0 and 1000, or clear it");
    }
    setSavingRate(true);
    try {
      await api.patch(`/admin/teacher-apps/${detail.application_id}/rate`, { hourly_rate: value });
      setDetail((d) => ({ ...d, preferences: { ...d.preferences, hourly_rate: value } }));
      toast.success("Hourly rate updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update rate"); }
    finally { setSavingRate(false); }
  };

  const act = async (action) => {
    try {
      const r = await api.post(`/admin/teacher-apps/${detail.application_id}/action`, {
        action, note: note || null, badges, interview_at: interviewAt || null,
      });
      setDetail({ ...detail, ...r.data });
      toast.success(`Action "${action}" applied`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  if (!data) return <div className="max-w-5xl mx-auto hifz-card h-64 animate-pulse-soft bg-sand-200/50" />;

  if (detail) {
    const a = detail;
    const rec = a.recitation;
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="teacher-app-detail">
        <button onClick={() => setDetail(null)} className="btn-ghost text-xs" data-testid="detail-back"><ChevronLeft className="h-3.5 w-3.5" /> All applications</button>
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display font-semibold text-3xl text-forest-700">{a.personal?.full_name || a.user?.name}</h1>
            <div className="text-sm text-muted-foreground">{a.user?.email} · {a.personal?.country} · {(a.personal?.languages || []).join(", ")}</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {(a.preferences?.categories || []).map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-sand-200">{c}</span>)}
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_CLS[a.status] || "bg-sand-200"}`} data-testid="detail-status">
            {a.status.replace(/_/g, " ")}
          </span>
        </header>

        <section className="hifz-card space-y-2 text-sm" data-testid="detail-profile">
          <h2 className="font-display font-semibold text-lg text-forest-700">Professional profile</h2>
          <div><strong>Experience:</strong> {a.professional?.years_teaching || "?"} years · {a.professional?.institution || "—"} · {a.professional?.position || "—"}</div>
          <div><strong>Bio:</strong> {a.professional?.bio || "—"}</div>
          <div><strong>Philosophy:</strong> {a.professional?.philosophy || "—"}</div>
          <div><strong>Can teach:</strong> {(a.preferences?.can_teach || []).join(", ") || "—"} · <strong>Mode:</strong> {a.preferences?.mode || "—"}</div>
          <div className="flex items-center gap-2 flex-wrap" data-testid="rate-editor">
            <strong>Hourly rate:</strong>
            <span className="text-muted-foreground">$</span>
            <input
              type="number" min="0" max="1000" step="1" placeholder="—"
              value={rateInput} onChange={(e) => setRateInput(e.target.value)}
              className="w-24 rounded-lg border border-sand-300 bg-white px-2 py-1 text-sm"
              data-testid="rate-input"
            />
            <span className="text-muted-foreground">/hr</span>
            <button onClick={saveRate} disabled={savingRate} className="btn-ghost text-xs" data-testid="rate-save">
              {savingRate ? "Saving…" : "Save"}
            </button>
            <span className="text-[11px] text-muted-foreground w-full">
              This is the teacher's own figure — it's what shows on their profile and what a booking charges. Editing it here changes that everywhere.
            </span>
          </div>
        </section>

        <section className="hifz-card space-y-3" data-testid="detail-evidence">
          <h2 className="font-display font-semibold text-lg text-forest-700">Evidence</h2>
          <div className="text-sm"><strong>Qualifications ({a.qualifications?.length || 0}):</strong></div>
          <div className="flex flex-wrap gap-2">
            {(a.qualifications || []).map((d) => (
              <a key={d.doc_id} href={MEDIA(d.key)} target="_blank" rel="noopener noreferrer"
                className="btn-ghost text-xs" data-testid={`doc-${d.doc_id}`}>
                📄 {d.kind}: {d.filename}
              </a>
            ))}
          </div>
          {rec && (
            <div className="rounded-xl bg-sand-200/60 p-3">
              <div className="text-sm font-semibold text-forest-700">Sample recitation — {rec.surah_name} {rec.ayah_start}–{rec.ayah_end}</div>
              <audio controls src={MEDIA(rec.key)} className="w-full mt-2" data-testid="detail-recitation-audio" />
              <div className="mt-2 grid grid-cols-4 gap-1.5 text-center">
                {Object.entries(rec.ai_score || {}).map(([k, v]) => (
                  <div key={k} className="rounded bg-white px-1 py-1.5">
                    <div className={`text-sm font-semibold ${k === "overall" ? "text-gold-600" : "text-forest-700"}`}>{v}</div>
                    <div className="text-[8px] uppercase text-muted-foreground">{k}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {a.demo && (
            <div>
              <div className="text-sm font-semibold text-forest-700 mb-1">Teaching demo</div>
              <video controls className="w-full rounded-xl max-h-72" src={MEDIA(a.demo.key)} data-testid="detail-demo-video" />
            </div>
          )}
          {a.assessment && (
            <div className="text-sm" data-testid="detail-assessment">
              <strong>Knowledge assessment:</strong> {a.assessment.percent}% ({a.assessment.score}/{a.assessment.total}) — {a.assessment.passed ? "PASSED" : "failed"}
              <div className="mt-1 flex flex-wrap gap-1.5">
                {Object.entries(a.assessment.per_topic || {}).map(([t, r]) => (
                  <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-sand-200">{t}: {r.correct}/{r.total}</span>
                ))}
              </div>
            </div>
          )}
          {(a.simulations || []).map((s) => (
            <div key={s.scenario_id} className="rounded-xl bg-sand-200/60 p-3" data-testid={`detail-sim-${s.scenario_id}`}>
              <div className="text-sm font-semibold text-forest-700">Simulation: {s.scenario_id}</div>
              {s.kind === "video"
                ? <video controls className="w-full rounded-lg max-h-60 mt-2" src={MEDIA(s.key)} />
                : <audio controls className="w-full mt-2" src={MEDIA(s.key)} />}
            </div>
          ))}
        </section>

        {a.ai_report && (
          <section className="hifz-card space-y-2 text-sm" data-testid="detail-ai-report">
            <h2 className="font-display font-semibold text-lg text-forest-700">AI Teacher Report</h2>
            <p>{a.ai_report.summary}</p>
            <div><strong>Recitation:</strong> {a.ai_report.recitation_quality}</div>
            <div><strong>Knowledge:</strong> {a.ai_report.knowledge_score}</div>
            <div><strong>Communication:</strong> {a.ai_report.communication}</div>
            {a.ai_report.strengths?.length > 0 && <div><strong className="text-emerald-600">Strengths:</strong> {a.ai_report.strengths.join(" · ")}</div>}
            {a.ai_report.improvements?.length > 0 && <div><strong className="text-terracotta-500">Improve:</strong> {a.ai_report.improvements.join(" · ")}</div>}
            {a.ai_report.suggested_training?.length > 0 && <div><strong className="text-gold-600">Training:</strong> {a.ai_report.suggested_training.join(" · ")}</div>}
          </section>
        )}

        <section className="hifz-card space-y-3" data-testid="detail-actions">
          <h2 className="font-display font-semibold text-lg text-forest-700">Decision</h2>
          {meta && (
            <div>
              <div className="text-xs font-semibold text-forest-700 mb-1">Verification badges (applied with "Verify")</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(meta.badges).map(([k, label]) => (
                  <button key={k} onClick={() => setBadges((b) => b.includes(k) ? b.filter((x) => x !== k) : [...b, k])}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                      badges.includes(k) ? "bg-forest-700 text-sand-100 border-forest-700" : "border-sand-300 hover:bg-sand-200"}`}
                    title={label} data-testid={`badge-${k}`}>
                    {label.split(" — ")[0]}
                  </button>
                ))}
              </div>
            </div>
          )}
          <textarea rows={2} placeholder="Note to applicant (optional)…" value={note} onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm" data-testid="action-note" />
          <div className="flex items-center gap-2 flex-wrap">
            <input type="datetime-local" value={interviewAt} onChange={(e) => setInterviewAt(e.target.value)}
              className="rounded-xl border border-sand-300 bg-white px-3 py-1.5 text-xs" data-testid="interview-at" />
            <button onClick={() => act("schedule_interview")} className="btn-ghost text-xs" data-testid="act-interview"><CalendarClock className="h-3.5 w-3.5" /> Schedule interview</button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => act("approve")} className="btn-primary text-xs" data-testid="act-approve"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
            <button onClick={() => act("verify")} className="btn-accent text-xs" data-testid="act-verify"><ShieldCheck className="h-3.5 w-3.5" /> Verify (+badges)</button>
            <button onClick={() => act("feature")} className="btn-ghost text-xs" data-testid="act-feature"><Star className="h-3.5 w-3.5" /> Feature</button>
            <button onClick={() => act("request_changes")} className="btn-ghost text-xs" data-testid="act-changes"><MessageSquareWarning className="h-3.5 w-3.5" /> Request changes</button>
            <button onClick={() => act("reject")} className="btn-ghost text-xs text-red-500" data-testid="act-reject"><XCircle className="h-3.5 w-3.5" /> Reject</button>
            <button onClick={() => act("suspend")} className="btn-ghost text-xs" data-testid="act-suspend"><PauseCircle className="h-3.5 w-3.5" /> Suspend</button>
            <button onClick={() => act("reactivate")} className="btn-ghost text-xs" data-testid="act-reactivate"><PlayCircle className="h-3.5 w-3.5" /> Reactivate</button>
          </div>
          <div className="text-xs text-muted-foreground">
            History: {(a.status_history || []).map((h) => h.status).join(" → ")}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="teacher-apps-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Admin · Teachers Network</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">Teacher Applications</h1>
      </header>
      <div className="flex flex-wrap gap-1.5" data-testid="status-filters">
        <button onClick={() => { setFilter(""); load(""); }} data-testid="filter-all"
          className={`px-2.5 py-1 rounded-full text-xs border ${!filter ? "bg-forest-700 text-sand-100 border-forest-700" : "border-sand-300"}`}>
          All ({Object.values(data.counts).reduce((a, b) => a + b, 0)})
        </button>
        {PIPELINE.map((s) => (
          <button key={s} onClick={() => { setFilter(s); load(s); }}
            className={`px-2.5 py-1 rounded-full text-xs border capitalize ${filter === s ? "bg-forest-700 text-sand-100 border-forest-700" : "border-sand-300"}`}
            data-testid={`filter-${s}`}>
            {s.replace(/_/g, " ")} ({data.counts[s] || 0})
          </button>
        ))}
      </div>
      {data.applications.length === 0 ? (
        <div className="hifz-card border-dashed text-center p-10 text-sm text-muted-foreground" data-testid="apps-empty">No applications in this stage.</div>
      ) : (
        <div className="space-y-2">
          {data.applications.map((a) => (
            <button key={a.application_id} onClick={() => open(a.application_id)}
              className="hifz-card hifz-card-hover w-full text-left flex flex-wrap items-center justify-between gap-3" data-testid={`app-row-${a.application_id}`}>
              <div>
                <div className="font-display font-semibold text-forest-700">{a.name || a.email}</div>
                <div className="text-xs text-muted-foreground">{a.country || "—"} · {(a.categories || []).slice(0, 3).join(", ")}</div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                {a.hourly_rate != null && <span>${a.hourly_rate}/hr</span>}
                {a.recitation_overall != null && <span>🎙 {a.recitation_overall}</span>}
                {a.assessment_percent != null && <span>🧠 {a.assessment_percent}%</span>}
                <span className={`px-2.5 py-1 rounded-full font-semibold capitalize ${STATUS_CLS[a.status] || "bg-sand-200"}`}>{a.status.replace(/_/g, " ")}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
